// Колоните на „Дневни отчети → Персонални".
//
// КЛИЕНТСКИ режим: `GET /reports` връща целия период наведнъж (`items` без
// страниране), затова сортирането, филтрите и редът „Общо" се смятат в
// браузъра, а чеклистът по сервитьор вади стойностите си от самите редове —
// показва точно тези, които имат отчет в периода, вместо целия списък с
// потребители.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { parseDecimal } from "../../../../components/dataTable";
import type { Report } from "../../../../api/reports";
import { formatWorkDay } from "./personalReportsMath";

const columnHelper = createColumnHelper<Report>();

/** `showOther` — има ли в периода платено извън в брой и карта. */
export const usePersonalReportsColumns = (showOther: boolean) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.accessor((row) => row.report_number, {
        id: "report_number",
        header: t("reportsPage.reportNumber"),
        size: 92,
        // Номерът е ЗНАК, не сума: собствен рендер с решетка, за да не се
        // подравни вдясно между парите като обикновено число.
        cell: (context) => (
          <span className="font-pos-mono text-[12.5px] font-bold tabular-nums text-ink">
            {`#${context.getValue()}`}
          </span>
        ),
        meta: {
          alwaysVisible: true,
          filter: { kind: "text" },
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor((row) => row.report_date, {
        id: "report_date",
        header: t("reportsPage.dailyWorkDay"),
        size: 132,
        // Подредбата тече върху суровия ISO низ — за него лексикографското
        // сравнение Е хронологично. Клетката НЕ е `dt`, защото работният ден
        // няма час и `dt` би изписала „00:00" под всеки ред.
        cell: (context) => (
          <span className="font-pos-mono text-[12.5px] font-semibold tabular-nums text-ink">
            {formatWorkDay(context.getValue())}
          </span>
        ),
        meta: {
          filter: { kind: "dateRange" },
          excel: { format: "date" },
        },
      }),

      columnHelper.accessor((row) => row.user_name ?? "", {
        id: "user_name",
        header: t("reportsPage.waiter"),
        size: 190,
        meta: {
          cell: { kind: "text" },
          // Заместникът на стария мултиселект над таблицата: филтърът стои в
          // колоната, която показва стойността.
          filter: { kind: "checklist" },
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor((row) => Number(row.total_bills_count ?? 0), {
        id: "total_bills_count",
        header: t("reportsPage.bills"),
        size: 96,
        meta: {
          cell: { kind: "num", decimals: 0 },
          filter: { kind: "numberRange" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((row) => parseDecimal(row.cash_amount), {
        id: "cash_amount",
        header: t("reportsPage.cashAmount"),
        size: 126,
        meta: {
          cell: { kind: "money" },
          total: "sum",
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor((row) => parseDecimal(row.card_amount), {
        id: "card_amount",
        header: t("reportsPage.cardAmount"),
        size: 126,
        meta: {
          cell: { kind: "money" },
          total: "sum",
          excel: { format: "money" },
        },
      }),

      // Колоната се появява само когато в периода Е платено с трети метод —
      // иначе цяла нулева колона би отнемала място от сумите, които се четат.
      ...(showOther
        ? [
            columnHelper.accessor((row) => parseDecimal(row.other_amount), {
              id: "other_amount",
              header: t("reportsPage.dailyOther"),
              size: 126,
              meta: {
                cell: { kind: "money" },
                total: "sum",
                excel: { format: "money" },
              },
            }),
          ]
        : []),

      columnHelper.accessor((row) => parseDecimal(row.total_amount), {
        id: "total_amount",
        header: t("reportsPage.dailyColTotal"),
        size: 138,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),

      // Кога е СЪСТАВЕН отчетът — рядко нужно, но иначе няма откъде да се
      // вземе; стои в панела „Колони" и се пали с една отметка.
      columnHelper.accessor((row) => row.created_at, {
        id: "created_at",
        header: t("reportsPage.createdAt"),
        size: 132,
        meta: {
          cell: { kind: "dt", withDate: "always" },
          defaultHidden: true,
          excel: { format: "date" },
        },
      }),
    ],
    [t, showOther],
  );
};
